import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import { almacen } from '../api';
import { Cargando, ConAyuda, Fallo, Filtro, Pastilla, Tarjeta, Vacio } from '../componentes';
import { cantidad, dia } from '../formato';
import { mayuscula, useVocabulario } from '../vocabulario';

/**
 * Qué pedir por lo que rota (9 oct).
 *
 * La bodega compra cuando pasa el distribuidor: lo que necesita saber es
 * cuánto vende al día de cada cosa y si le alcanza hasta la próxima visita.
 * «Por mínimo» pide cuando algo ya bajó; «Por lo vendido» es la lista del
 * mercado de quien compra cada madrugada. Aquí se elige cuántos días mirar y
 * para cuántos días comprar, y sale lo que falta, lo que se acaba antes
 * primero.
 */
export function ListaPorRotacion() {
  const vocabulario = useVocabulario();
  const [dias, setDias] = useState(28);
  const [cubrir, setCubrir] = useState(7);
  const consulta = useQuery({
    queryKey: ['por-rotacion', dias, cubrir],
    queryFn: () => almacen.rotationList(dias, cubrir),
  });
  const lista = consulta.data;

  return (
    <>
      <div className="filtros">
        {[14, 28, 56].map((n) => (
          <Filtro key={n} texto={`Últimos ${n} días`} activo={dias === n} onClick={() => setDias(n)} />
        ))}
      </div>
      <div className="filtros">
        {[3, 7, 14].map((n) => (
          <Filtro key={n} texto={`Para ${n} días`} activo={cubrir === n} onClick={() => setCubrir(n)} />
        ))}
      </div>
      {consulta.isError ? <Fallo error={consulta.error} onReintentar={() => consulta.refetch()} /> : null}
      {consulta.isLoading || lista === undefined ? (
        consulta.isError ? null : <Cargando que="lo que rota" />
      ) : (
        <>
          <p className="nota">
            Lo vendido del {dia(lista.from)} al {dia(lista.to)} —hoy no cuenta, va a medias—, por día, para
            cubrir {lista.coverDays} días, menos lo que hay y lo ya pedido. Lo vencido no cuenta como que hay.
          </p>

          {lista.groups.length === 0 ? (
            <Vacio
              titulo="Te alcanza para esos días"
              mensaje={`Con lo que hay cubres los próximos ${lista.coverDays} días al ritmo de venta de los últimos ${lista.days}. Lo que no se vendió no aparece aquí.`}
            />
          ) : (
            lista.groups.map((grupo) => (
              <Tarjeta key={grupo.supplierId ?? 'sin-proveedor'}>
                <div className="tarjeta__cabeza">
                  <h2 className="tarjeta__titulo">{grupo.supplier}</h2>
                  {grupo.supplierId === null ? <Pastilla texto="Sin proveedor" tono="aviso" /> : null}
                </div>
                <div className="tabla-envoltura">
                  <table>
                    <thead>
                      <tr>
                        <th>{mayuscula(vocabulario.insumo)}</th>
                        <th className="numero">
                          <ConAyuda ayuda="Lo que sale en un día, en promedio, por ventas y producción.">Al día</ConAyuda>
                        </th>
                        <th className="numero">
                          <ConAyuda ayuda="Lo que queda hoy, sin lo vencido.">Hay</ConAyuda>
                        </th>
                        <th className="numero">
                          <ConAyuda ayuda="Para cuántos días alcanza lo que hay, al ritmo de venta.">Alcanza</ConAyuda>
                        </th>
                        <th className="numero">
                          <ConAyuda ayuda="Lo que ya está en una orden de compra sin recibir.">Pedido</ConAyuda>
                        </th>
                        <th className="numero">Pedir</th>
                      </tr>
                    </thead>
                    <tbody>
                      {grupo.lines.map((line) => (
                        <tr key={line.supplyId}>
                          <td>
                            <strong>{line.name}</strong>
                          </td>
                          <td className="numero mono apagado">{cantidad(line.dailyAverage)}</td>
                          <td className="numero mono apagado">{cantidad(line.onHand)}</td>
                          <td className="numero mono">{alcanza(line.daysLeft)}</td>
                          <td className="numero mono apagado">{cantidad(line.onOrder)}</td>
                          <td className="numero mono">
                            <strong>{cantidad(line.suggested)}</strong> <span className="apagado">{line.unit}</span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Tarjeta>
            ))
          )}
        </>
      )}
    </>
  );
}

/** «Hoy» si ya no alcanza ni para mañana; si no, los días. */
function alcanza(dias: number | null) {
  if (dias === null) return '—';
  if (dias === 0) return <Pastilla texto="Hoy" tono="aviso" />;
  return dias === 1 ? '1 día' : `${dias} días`;
}
